import axios from "axios";
import { API_ENDPOINTS } from "../config/api";

/**
 * Axios instance with automatic token refresh
 *
 * This instance automatically:
 * 1. Adds access token to all requests
 * 2. Intercepts 401 errors
 * 3. Refreshes access token using refresh token
 * 4. Retries failed request with new token
 * 5. Logs out user if refresh token is invalid
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
// Queue of failed requests waiting for token refresh
let failedRequestsQueue = [];

/**
 * Process queued requests after token refresh
 */
const processQueue = (error, token = null) => {
  failedRequestsQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });

  failedRequestsQueue = [];
};

/**
 * Request Interceptor - Add access token to all requests
 */
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("cia_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor - Handle token expiration and refresh
 */
apiClient.interceptors.response.use(
  (response) => {
    // If response is successful, just return it
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Check if error is 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Check if this is a token expiration (not invalid credentials)
      const isTokenExpired =
        error.response?.data?.code === "TOKEN_EXPIRED" ||
        error.response?.data?.message?.includes("expired");

      // Don't try to refresh on login/register/refresh endpoints
      const isAuthEndpoint =
        originalRequest.url.includes("/auth/login") ||
        originalRequest.url.includes("/auth/register") ||
        originalRequest.url.includes("/auth/refresh");

      if (isTokenExpired && !isAuthEndpoint) {
        if (isRefreshing) {
          // If already refreshing, queue this request
          return new Promise((resolve, reject) => {
            failedRequestsQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return apiClient(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        // Mark that we're retrying this request
        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = localStorage.getItem("cia_refresh_token");

        if (!refreshToken) {
          // No refresh token, log out
          console.error("No refresh token available");
          processQueue(new Error("No refresh token"), null);
          isRefreshing = false;
          logout();
          return Promise.reject(error);
        }

        try {
          // Call refresh endpoint
          const response = await axios.post(
            API_ENDPOINTS.REFRESH_TOKEN,
            { refreshToken },
            {
              headers: { "Content-Type": "application/json" },
              withCredentials: true,
            }
          );

          if (response.data.success && response.data.data?.tokens) {
            const { accessToken, refreshToken: newRefreshToken } =
              response.data.data.tokens;

            // Update tokens in localStorage
            localStorage.setItem("cia_token", accessToken);
            localStorage.setItem("cia_refresh_token", newRefreshToken);

            // Update authorization header
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;

            // Process queued requests
            processQueue(null, accessToken);

            isRefreshing = false;

            // Retry original request
            return apiClient(originalRequest);
          } else {
            throw new Error("Invalid refresh response");
          }
        } catch (refreshError) {
          console.error("Token refresh failed:", refreshError);

          // Process queued requests with error
          processQueue(refreshError, null);
          isRefreshing = false;

          // Refresh token is invalid or expired, log out
          logout();

          return Promise.reject(refreshError);
        }
      }
    }

    // For all other errors, just reject
    return Promise.reject(error);
  }
);

/**
 * Logout user - clear localStorage and redirect to login
 */
const logout = () => {
  localStorage.removeItem("cia_user");
  localStorage.removeItem("cia_token");
  localStorage.removeItem("cia_refresh_token");

  // Redirect to login page
  window.location.href = "/login";
};

export default apiClient;
